// EditCategoryModal.tsx — Co Marca — Editar Categoría
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Edit, Building2, CheckCircle2 } from 'lucide-react';
import { updateCategory, fetchActividadesEconomicas, fetchSucursalesList } from '../../services/categoryService';
import ImageUpload from '../Upload/ImageUpload';

interface Category {
    id: number;
    nombre: string;
    descripcion?: string;
    imagen?: string;
    estado: boolean;
    fecha_creacion: string;
    sucursales_details?: { id: number; nombre: string }[];
    sucursales?: (number | { id: number })[];
    actividad_economica?: number | null;
}

interface EditCategoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    category: Category | null;
    onUpdated: () => void;
}

interface ActividadOption { id: number; codigo: string; descripcion: string; }

interface SucursalOption {
    id: number;
    nombre: string;
    activo: boolean;
}

const colors = {
    primary:        '#c1348b',
    primaryDark:    '#8f1f64',
    secondary:      '#61257b',
    secondaryLight: '#8b3fa8',
    accentDark:     '#ea8406',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');
.em-modal * { font-family: 'DM Sans', sans-serif; box-sizing: border-box; }
@keyframes emFadeIn  { from { opacity:0; } to { opacity:1; } }
@keyframes emSlideUp { from { opacity:0; transform:translateY(24px) scale(.98); } to { opacity:1; transform:translateY(0) scale(1); } }
@keyframes spin { to { transform:rotate(360deg); } }
.em-overlay { animation: emFadeIn  .22s ease both; }
.em-dialog  { animation: emSlideUp .32s cubic-bezier(.16,1,.3,1) both; }
.em-input {
    width:100%; padding:10px 14px; border-radius:10px;
    border:1.5px solid #e5e7eb; background:#fafafa;
    font-size:.88rem; color:#374151;
    outline:none; transition:all .2s;
}
.em-input:focus { border-color:${colors.secondary}; box-shadow:0 0 0 3px ${colors.secondary}12; background:#fff; }
.em-textarea { resize:none; min-height:80px; }
.em-btn-primary {
    display:inline-flex; align-items:center; gap:7px;
    background:linear-gradient(135deg,${colors.secondary},#004d7a);
    color:#fff; border:none; border-radius:11px;
    padding:10px 22px; font-size:.88rem; font-weight:800;
    cursor:pointer; box-shadow:0 4px 14px rgba(97, 37, 123,.28);
    transition:all .22s cubic-bezier(.16,1,.3,1);
}
.em-btn-primary:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 8px 22px rgba(97, 37, 123,.34); }
.em-btn-primary:disabled { background:#e2e8f0; color:#94a3b8; box-shadow:none; cursor:not-allowed; }
.em-btn-ghost {
    display:inline-flex; align-items:center; gap:7px;
    background:#f8fafc; border:1.5px solid #e2e8f0;
    color:#374151; border-radius:11px;
    padding:10px 18px; font-size:.88rem; font-weight:700;
    cursor:pointer; transition:all .2s;
}
.em-btn-ghost:hover { background:#f1f5f9; }
.em-suc-item { transition:background .15s; border-radius:9px; }
.em-suc-item:hover { background:#f0f9ff; }
.custom-scroll::-webkit-scrollbar { width:5px; }
.custom-scroll::-webkit-scrollbar-track { background:transparent; }
.custom-scroll::-webkit-scrollbar-thumb { background:rgba(97, 37, 123,.3); border-radius:20px; }
`;

const EditCategoryModal: React.FC<EditCategoryModalProps> = ({ isOpen, onClose, category, onUpdated }) => {
    const [nombre, setNombre]         = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [imagen, setImagen]         = useState<File | null>(null);
    const [actividadEconomica, setActividadEconomica] = useState<number | ''>('');
    const [actividades, setActividades] = useState<ActividadOption[]>([]);
    const [estado, setEstado]         = useState(true);
    const [sucursalesDisponibles, setSucursalesDisponibles] = useState<SucursalOption[]>([]);
    const [selectedSucursales, setSelectedSucursales]       = useState<number[]>([]);
    const [loading, setLoading]       = useState(false);
    const [error, setError]           = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) {
            Promise.all([fetchSucursalesList(), fetchActividadesEconomicas()])
                .then(([sucursales, actividades]) => {
                    setSucursalesDisponibles(sucursales.filter((s: SucursalOption) => s.activo));
                    setActividades(actividades);
                })
                .catch(err => console.error('Error cargando sucursales', err));
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && category) {
            setNombre(category.nombre);
            setDescripcion(category.descripcion || '');
            setEstado(category.estado);
            setImagen(null);
            setActividadEconomica(category.actividad_economica || '');
            setError(null);
            let ids: number[] = [];
            if (category.sucursales_details?.length) ids = category.sucursales_details.map(s => s.id);
            else if (category.sucursales?.length)    ids = category.sucursales.map(s => typeof s === 'object' ? s.id : Number(s));
            setSelectedSucursales(ids);
        }
    }, [isOpen, category]);

    const toggleSucursal = (id: number) =>
        setSelectedSucursales(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true); setError(null);
        try {
            await updateCategory(category!.id, { nombre, descripcion, imagen, estado, sucursales: selectedSucursales, actividad_economica: actividadEconomica || null });
            onUpdated(); onClose();
        } catch {
            setError('Error al actualizar la categoría');
        } finally { setLoading(false); }
    };

    if (!isOpen || !category) return null;

    return createPortal(
        <>
            <style>{css}</style>
            <div className="em-modal" style={{ position:'fixed', inset:0, zIndex:99999, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
                {/* Overlay */}
                <div className="em-overlay" onClick={onClose} style={{ position:'absolute', inset:0, background:'rgba(15,23,42,.55)', backdropFilter:'blur(6px)' }}/>

                {/* Dialog */}
                <div className="em-dialog custom-scroll" style={{ position:'relative', background:'#fff', borderRadius:'24px', width:'100%', maxWidth:'580px', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 32px 80px rgba(0,0,0,.18)', border:'1.5px solid #f1f5f9' }}>

                    {/* Barra top */}
                    <div style={{ height:'4px', background:`linear-gradient(90deg,${colors.secondary},${colors.secondaryLight},${colors.accentDark},${colors.primary})`, borderRadius:'24px 24px 0 0' }}/>

                    {/* Header */}
                    <div style={{ padding:'24px 28px 0', display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
                        <div>
                            <div style={{ display:'flex', alignItems:'center', gap:'7px', marginBottom:'8px' }}>
                                <div style={{ width:'36px', height:'36px', borderRadius:'11px', background:`linear-gradient(135deg,${colors.secondary},#004d7a)`, display:'flex', alignItems:'center', justifyContent:'center', boxShadow:`0 4px 12px ${colors.secondary}30` }}>
                                    <Edit size={17} color="#fff"/>
                                </div>
                                <div>
                                    <h3 style={{ fontSize:'1.1rem', fontWeight:'900', color:'#0f172a', letterSpacing:'-.02em' }}>Editar Categoría</h3>
                                    <p style={{ fontSize:'.72rem', color:'#94a3b8', fontWeight:'500' }}>Modifica los datos de <strong style={{ color:'#374151' }}>{category.nombre}</strong></p>
                                </div>
                            </div>
                            <div style={{ display:'flex', gap:'4px', marginTop:'4px' }}>
                                {[colors.secondary,colors.secondaryLight,colors.accentDark,colors.primary].map((c,i)=>(
                                    <div key={i} style={{ height:'2px', width:i===0?'18px':'6px', borderRadius:'2px', background:c }}/>
                                ))}
                            </div>
                        </div>
                        <button onClick={onClose} style={{ width:'32px', height:'32px', borderRadius:'10px', border:'1.5px solid #f1f5f9', background:'#f8fafc', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0 }}
                            onMouseEnter={e=>{ (e.currentTarget as HTMLButtonElement).style.background='#f1f5f9'; }}
                            onMouseLeave={e=>{ (e.currentTarget as HTMLButtonElement).style.background='#f8fafc'; }}>
                            <X size={15} color="#64748b"/>
                        </button>
                    </div>

                    {/* Body */}
                    <form onSubmit={handleSubmit} style={{ padding:'20px 28px 28px', display:'flex', flexDirection:'column', gap:'18px' }}>

                        {error && (
                            <div style={{ display:'flex', alignItems:'center', gap:'8px', background:`${colors.primary}08`, border:`1.5px solid ${colors.primary}22`, borderRadius:'10px', padding:'10px 14px' }}>
                                <X size={14} color={colors.primary}/>
                                <span style={{ fontSize:'.82rem', color:colors.primaryDark, fontWeight:'600' }}>{error}</span>
                            </div>
                        )}

                        {/* Nombre + Descripción */}
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
                            <div>
                                <label style={{ display:'block', fontSize:'.75rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>Nombre *</label>
                                <input type="text" value={nombre} onChange={e=>setNombre(e.target.value)} required className="em-input" placeholder="Nombre de categoría"/>
                            </div>
                            <div>
                                <label style={{ display:'block', fontSize:'.75rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>Descripción</label>
                                <textarea value={descripcion} onChange={e=>setDescripcion(e.target.value)} className="em-input em-textarea" style={{ height:'42px', minHeight:'42px' }} placeholder="Descripción opcional..."/>
                            </div>
                        </div>

                        <div>
                            <label style={{ display:'block', fontSize:'.75rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>Actividad económica SIN *</label>
                            <select className="em-input" required value={actividadEconomica} onChange={e=>setActividadEconomica(e.target.value ? Number(e.target.value) : '')}>
                                <option value="">Selecciona una actividad</option>
                                {actividades.map(a=><option key={a.id} value={a.id}>{a.codigo} - {a.descripcion}</option>)}
                            </select>
                        </div>

                        {/* Sucursales */}
                        <div>
                            <label style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'.75rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'10px' }}>
                                <Building2 size={12} color={colors.secondary}/> Sucursales activas
                            </label>
                            <div className="custom-scroll" style={{ background:'#f8fafc', border:'1.5px solid #f1f5f9', borderRadius:'14px', padding:'12px', maxHeight:'140px', overflowY:'auto', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px' }}>
                                {sucursalesDisponibles.length > 0 ? sucursalesDisponibles.map(suc => {
                                    const checked = selectedSucursales.includes(suc.id);
                                    return (
                                        <label key={suc.id} className="em-suc-item" style={{ display:'flex', alignItems:'center', gap:'8px', padding:'7px 10px', cursor:'pointer' }}>
                                            <div onClick={()=>toggleSucursal(suc.id)} style={{ width:'18px', height:'18px', borderRadius:'5px', border:`1.5px solid ${checked ? colors.secondary : '#d1d5db'}`, background:checked ? colors.secondary : '#fff', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'all .15s', cursor:'pointer' }}>
                                                {checked && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4l3 3 5-6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                            </div>
                                            <input type="checkbox" checked={checked} onChange={()=>toggleSucursal(suc.id)} style={{ display:'none' }}/>
                                            <span style={{ fontSize:'.82rem', color:'#374151', fontWeight: checked ? '700' : '500' }}>{suc.nombre}</span>
                                        </label>
                                    );
                                }) : (
                                    <p style={{ fontSize:'.78rem', color:'#94a3b8', gridColumn:'span 2', textAlign:'center', padding:'8px' }}>Cargando sucursales...</p>
                                )}
                            </div>
                        </div>

                        {/* Imagen */}
                        <div>
                            <label style={{ display:'block', fontSize:'.75rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>Imagen</label>
                            <ImageUpload setImagen={setImagen} currentImage={category.imagen}/>
                        </div>

                        {/* Estado toggle */}
                        <div style={{ background:'#f8fafc', border:'1.5px solid #f1f5f9', borderRadius:'14px', padding:'14px 16px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                            <div>
                                <p style={{ fontSize:'.85rem', fontWeight:'700', color:'#0f172a' }}>Estado de la categoría</p>
                                <p style={{ fontSize:'.72rem', color: estado ? colors.secondaryLight : '#94a3b8', fontWeight:'600', marginTop:'2px' }}>
                                    {estado ? '● Activa' : '○ Inactiva'}
                                </p>
                            </div>
                            <div onClick={()=>setEstado(!estado)} style={{ width:'44px', height:'24px', borderRadius:'100px', background: estado ? colors.secondary : '#d1d5db', position:'relative', cursor:'pointer', transition:'background .25s', flexShrink:0 }}>
                                <div style={{ position:'absolute', top:'3px', left: estado ? '23px' : '3px', width:'18px', height:'18px', borderRadius:'50%', background:'#fff', boxShadow:'0 1px 4px rgba(0,0,0,.2)', transition:'left .25s' }}/>
                            </div>
                        </div>

                        {/* Footer */}
                        <div style={{ display:'flex', justifyContent:'flex-end', gap:'10px', paddingTop:'4px' }}>
                            <button type="button" onClick={onClose} className="em-btn-ghost">Cancelar</button>
                            <button type="submit" disabled={loading} className="em-btn-primary">
                                {loading
                                    ? <><span style={{ width:'13px', height:'13px', border:'2px solid rgba(255,255,255,.4)', borderTopColor:'#fff', borderRadius:'50%', animation:'spin .7s linear infinite', display:'inline-block' }}/> Actualizando...</>
                                    : <><CheckCircle2 size={14}/> Actualizar Categoría</>
                                }
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>,
        document.body
    );
};

export default EditCategoryModal;

